// src/config/osintTools.js

export const osintTools = {
  username: [
    {
      name: 'GitHub',
      icon: '💻',
      category: 'Development',
      search: async (username) => {
        try {
          const res = await fetch(`https://api.github.com/users/${username}`, {
            headers: { 'Accept': 'application/vnd.github.v3+json' }
          });
          if (res.status === 404) return { found: false };
          if (!res.ok) throw new Error('API Error');
          
          const data = await res.json();
          return {
            found: true,
            data: {
              '🔗 Profile': `<a href="${data.html_url}" target="_blank" class="text-indigo-300 hover:text-indigo-200 underline">${data.html_url}</a>`,
              '👤 Name': data.name || 'Not provided',
              '📝 Bio': data.bio || 'No bio available',
              '📦 Repositories': data.public_repos.toString(),
              '👥 Followers': data.followers.toLocaleString(),
              '➡️ Following': data.following.toLocaleString(),
              '📍 Location': data.location || 'Not provided',
              '🏢 Company': data.company || 'Not provided',
              '🌐 Website': data.blog ? `<a href="${data.blog}" target="_blank" class="text-indigo-300 underline">${data.blog}</a>` : 'Not provided',
              '🐦 Twitter': data.twitter_username || 'Not provided',
              '📅 Created': new Date(data.created_at).toLocaleDateString()
            }
          };
        } catch (e) {
          return { error: e.message };
        }
      }
    },
    {
      name: 'Reddit',
      icon: '🤖',
      category: 'Social',
      search: async (username) => {
        try {
          const res = await fetch(`https://www.reddit.com/user/${username}/about.json`);
          if (res.status === 404) return { found: false };
          if (!res.ok) throw new Error('API Error');
          
          const json = await res.json();
          const data = json.data;
          return {
            found: true,
            data: {
              '🔗 Profile': `<a href="https://reddit.com/user/${username}" target="_blank" class="text-indigo-300 underline">reddit.com/u/${username}</a>`,
              '👤 Display Name': data.name,
              '⭐ Total Karma': data.total_karma.toLocaleString(),
              '📮 Post Karma': data.link_karma.toLocaleString(),
              '💬 Comment Karma': data.comment_karma.toLocaleString(),
              '📅 Account Age': new Date(data.created * 1000).toLocaleDateString(),
              '✉️ Verified Email': data.has_verified_email ? '✅ Yes' : '❌ No',
              '👑 Reddit Premium': data.is_gold ? '✅ Yes' : '❌ No',
              '🛡️ Moderator': data.is_mod ? '✅ Yes' : '❌ No'
            }
          };
        } catch (e) {
          return { error: e.message };
        }
      }
    },
    {
      name: 'Dev.to',
      icon: '💻',
      category: 'Development',
      search: async (username) => {
        try {
          const res = await fetch(`https://dev.to/api/users/by_username?url=${username}`);
          if (res.status === 404) return { found: false };
          if (!res.ok) throw new Error('API Error');
          
          const data = await res.json();
          return {
            found: true,
            data: {
              '🔗 Profile': `<a href="https://dev.to/${username}" target="_blank" class="text-indigo-300 underline">dev.to/${username}</a>`,
              '👤 Name': data.name || 'Not provided',
              '📝 Bio': data.summary || 'No bio',
              '📅 Joined': data.joined_at || 'Unknown',
              '🌐 Website': data.website_url ? `<a href="${data.website_url}" target="_blank" class="text-indigo-300 underline">${data.website_url}</a>` : 'Not provided',
              '🐦 Twitter': data.twitter_username || 'Not provided',
              '💻 GitHub': data.github_username || 'Not provided',
              '📍 Location': data.location || 'Not provided'
            }
          };
        } catch (e) {
          return { error: e.message };
        }
      }
    },
    {
      name: 'IDCrawl',
      icon: '🆔',
      category: 'Aggregator',
      search: async (username) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Search URL': `<a href="https://www.idcrawl.com/${username}" target="_blank" class="text-indigo-300 underline">idcrawl.com/${username}</a>`,
          '📋 Description': 'Aggregates profiles from Facebook, LinkedIn, Twitter, Instagram, TikTok',
          '⚡ Type': 'Multi-platform people search',
          '✅ Action': 'Click link to view aggregated results'
        }
      })
    },
    {
      name: 'Sherlock Project',
      icon: '🕵️',
      category: 'Tool',
      search: async (username) => ({
        found: true,
        manual: true,
        data: {
          '🔗 GitHub': `<a href="https://github.com/sherlock-project/sherlock" target="_blank" class="text-indigo-300 underline">github.com/sherlock-project</a>`,
          '⚡ Type': 'Command-line OSINT tool',
          '📋 Coverage': '400+ websites',
          '💻 Install': 'pip install sherlock-project',
          '🎯 Usage': `sherlock ${username}`,
          '✅ Features': 'Most comprehensive CLI tool for username investigation'
        }
      })
    },
    {
      name: 'Twitter/X',
      icon: '🐦',
      category: 'Social',
      search: async (username) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Profile': `<a href="https://twitter.com/${username}" target="_blank" class="text-indigo-300 underline">twitter.com/${username}</a>`,
          '🔗 Nitter': `<a href="https://nitter.net/${username}" target="_blank" class="text-indigo-300 underline">nitter.net/${username}</a>`,
          '📋 Note': 'API requires authentication',
          '✅ Action': 'Visit links to verify profile'
        }
      })
    },
    {
      name: 'Instagram',
      icon: '📸',
      category: 'Social',
      search: async (username) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Profile': `<a href="https://www.instagram.com/${username}/" target="_blank" class="text-indigo-300 underline">instagram.com/${username}</a>`,
          '🔗 ImgInn': `<a href="https://imginn.com/${username}/" target="_blank" class="text-indigo-300 underline">imginn.com/${username}</a>`,
          '✅ Action': 'Use alternative viewers if profile is private'
        }
      })
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      category: 'Professional',
      search: async (username) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Profile': `<a href="https://www.linkedin.com/in/${username}" target="_blank" class="text-indigo-300 underline">linkedin.com/in/${username}</a>`,
          '🔗 Search': `<a href="https://www.linkedin.com/search/results/all/?keywords=${username}" target="_blank" class="text-indigo-300 underline">Search LinkedIn</a>`,
          '📋 Note': 'May require login for full access',
          '✅ Type': 'Professional networking platform'
        }
      })
    }
  ],
  
  email: [
    {
      name: 'Email Format Check',
      icon: '✅',
      category: 'Validation',
      search: async (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = regex.test(email);
        const parts = email.split('@');
        
        if (parts.length !== 2) {
          return { error: 'Invalid email format' };
        }
        
        const [local, domain] = parts;
        const tld = domain.split('.').pop();
        
        return {
          found: isValid,
          data: {
            '✅ Format Valid': isValid ? '✅ Yes' : '❌ No',
            '👤 Local Part': local,
            '🌐 Domain': domain,
            '🏷️ TLD': tld,
            '🔗 Domain Info': `<a href="https://whois.domaintools.com/${domain}" target="_blank" class="text-indigo-300 underline">WHOIS Lookup</a>`
          }
        };
      }
    },
    {
      name: 'Have I Been Pwned',
      icon: '🔒',
      category: 'Security',
      search: async (email) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Check': `<a href="https://haveibeenpwned.com/" target="_blank" class="text-indigo-300 underline">haveibeenpwned.com</a>`,
          '📧 Email': email,
          '📋 Purpose': 'Check if email appeared in data breaches',
          '🔒 Privacy': 'Secure - does not store or log searches',
          '✅ Action': 'Enter email on site to check breach history'
        }
      })
    },
    {
      name: 'Hunter.io',
      icon: '🎯',
      category: 'Verification',
      search: async (email) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Verify': `<a href="https://hunter.io/email-verifier" target="_blank" class="text-indigo-300 underline">hunter.io/email-verifier</a>`,
          '📧 Email': email,
          '📋 Features': 'Email verification, domain search, company contacts',
          '✅ Action': 'Visit site to verify email and find related contacts'
        }
      })
    }
  ],
  
  domain: [
    {
      name: 'WHOIS Lookup',
      icon: '🌐',
      category: 'Registration',
      search: async (domain) => {
        domain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        return {
          found: true,
          manual: true,
          data: {
            '🌐 Domain': domain,
            '🔗 DomainTools': `<a href="https://whois.domaintools.com/${domain}" target="_blank" class="text-indigo-300 underline">WHOIS Lookup</a>`,
            '🔗 ICANN': `<a href="https://lookup.icann.org/en/lookup" target="_blank" class="text-indigo-300 underline">ICANN Lookup</a>`,
            '📋 Info': 'Registration date, registrar, contact info'
          }
        };
      }
    },
    {
      name: 'Security Analysis',
      icon: '🔒',
      category: 'Security',
      search: async (domain) => {
        domain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        return {
          found: true,
          manual: true,
          data: {
            '🌐 Domain': domain,
            '🔗 Security Headers': `<a href="https://securityheaders.com/?q=${domain}" target="_blank" class="text-indigo-300 underline">securityheaders.com</a>`,
            '🔗 SSL Labs': `<a href="https://www.ssllabs.com/ssltest/analyze.html?d=${domain}" target="_blank" class="text-indigo-300 underline">SSL Test</a>`,
            '📋 Analysis': 'Security headers, SSL/TLS configuration'
          }
        };
      }
    }
  ],
  
  image: [
    {
      name: 'Google Reverse Image',
      icon: '🔍',
      category: 'Search',
      search: async (query) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Search': `<a href="https://images.google.com/imghp" target="_blank" class="text-indigo-300 underline">Google Images</a>`,
          '📋 Method': 'Upload image or paste URL',
          '✅ Finds': 'Similar images, sources, sizes, visually similar',
          '🎯 Best for': 'General image source tracking'
        }
      })
    },
    {
      name: 'TinEye',
      icon: '👁️',
      category: 'Search',
      search: async (query) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Search': `<a href="https://tineye.com/" target="_blank" class="text-indigo-300 underline">tineye.com</a>`,
          '📋 Database': '60+ billion images indexed',
          '✅ Finds': 'Exact matches, modifications, original source',
          '🎯 Best for': 'Finding oldest version and tracking modifications'
        }
      })
    }
  ],
  
  social: [
    {
      name: 'Social-Searcher',
      icon: '🔎',
      category: 'Multi-Platform',
      search: async (query) => ({
        found: true,
        manual: true,
        data: {
          '🔗 Search': `<a href="https://www.social-searcher.com/" target="_blank" class="text-indigo-300 underline">social-searcher.com</a>`,
          '📋 Platforms': 'Twitter, Facebook, Instagram, YouTube, Reddit, etc.',
          '✅ Features': 'Real-time monitoring, sentiment analysis, statistics',
          '🎯 Search by': 'Keywords, hashtags, mentions, profiles'
        }
      })
    }
  ]
};